import React from "react";
import styles from "./TicketForm.module.css";

interface GuestContactFields {
  name: string;
  email: string;
  phone: string;
  errors: { name: string; email: string; phone: string };
  onNameChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onEmailChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onEmailBlur?: () => void;
  onPhoneChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

interface TicketFormValues {
  subject: string;
  description: string;
  priority: "low" | "medium" | "high" | "urgent";
  [key: string]: unknown;
}

interface TicketFormProps<T extends TicketFormValues> {
  ticketForm: T;
  setTicketForm: (form: T) => void;
  departments?: string[];
  selectedDepartment?: string;
  onDepartmentChange?: (dept: string) => void;
  showDepartmentSelect?: boolean;
  guestFields?: GuestContactFields;
  onSubmit: (e: React.FormEvent) => void;
  submitLabel?: string;
  intro?: string;
}

const guestInputClass = (hasError?: boolean) =>
  `${styles.guestInput} ${hasError ? styles.guestInputError : styles.guestInputValid}`;

export function TicketForm<T extends TicketFormValues>({
  ticketForm,
  setTicketForm,
  departments = [],
  selectedDepartment,
  onDepartmentChange,
  showDepartmentSelect,
  guestFields,
  onSubmit,
  submitLabel = "Create Ticket",
  intro = "No support staff is currently online. Please create a ticket and we'll get back to you soon.",
}: TicketFormProps<T>) {
  return (
    <form onSubmit={onSubmit} className={styles.form}>
      <p className={styles.intro}>{intro}</p>

      {guestFields && (
        <>
          <div className={styles.fieldGroup}>
            <input
              type="text"
              placeholder="Name"
              required
              value={guestFields.name}
              onChange={guestFields.onNameChange}
              className={guestInputClass(!!guestFields.errors.name)}
            />
            {guestFields.errors.name && (
              <p className={styles.errorText}>{guestFields.errors.name}</p>
            )}
          </div>

          <div className={styles.fieldGroup}>
            <input
              type="email"
              placeholder="Email"
              required
              value={guestFields.email}
              onChange={guestFields.onEmailChange}
              onBlur={guestFields.onEmailBlur}
              className={guestInputClass(!!guestFields.errors.email)}
            />
            {guestFields.errors.email && (
              <p className={styles.errorText}>{guestFields.errors.email}</p>
            )}
          </div>

          <div className={styles.fieldGroup}>
            <input
              type="tel"
              placeholder="Phone (e.g. +61 ### ### ###)"
              value={guestFields.phone}
              onChange={guestFields.onPhoneChange}
              pattern="[0-9+() -]*"
              maxLength={20}
              className={guestInputClass(!!guestFields.errors.phone)}
            />
            {guestFields.errors.phone && (
              <p className={styles.errorText}>{guestFields.errors.phone}</p>
            )}
          </div>
        </>
      )}

      {showDepartmentSelect && (
        <div>
          <label className={styles.label}>Department</label>
          <select
            required
            value={selectedDepartment}
            onChange={(e) => onDepartmentChange?.(e.target.value)}
            className={styles.select}
          >
            <option value="">Select Department</option>
            {departments.map((dept) => (
              <option key={dept} value={dept}>
                {dept}
              </option>
            ))}
          </select>
        </div>
      )}

      <div>
        <label className={styles.label}>Subject</label>
        <input
          type="text"
          required
          value={ticketForm.subject}
          onChange={(e) => setTicketForm({ ...ticketForm, subject: e.target.value })}
          className={styles.input}
        />
      </div>

      <div>
        <label className={styles.label}>Description</label>
        <textarea
          required
          value={ticketForm.description}
          onChange={(e) => setTicketForm({ ...ticketForm, description: e.target.value })}
          rows={4}
          className={styles.textarea}
        />
      </div>

      <div>
        <label className={styles.label}>Priority</label>
        <select
          value={ticketForm.priority}
          onChange={(e) =>
            setTicketForm({ ...ticketForm, priority: e.target.value as TicketFormValues["priority"] })
          }
          className={styles.select}
        >
          <option value="low">Low</option>
          <option value="medium">Medium</option>
          <option value="high">High</option>
          <option value="urgent">Urgent</option>
        </select>
      </div>

      <button type="submit" className={styles.submitBtn}>
        {submitLabel}
      </button>
    </form>
  );
}
